import React from 'react';
import { LucideIcon, CheckCircle, Info } from 'lucide-react';
import type { Locale } from '../../types/locale';

interface KeyPoint {
  icon?: LucideIcon;
  iconColor?: 'accent' | 'green' | 'yellow' | 'neutral';
  text: string;
}

interface KeyAnswerProps {
  // Section ID for jump links
  id?: string;

  // H2 heading
  heading: string;

  // Quick answer paragraph - optimized for featured snippets
  // This should be 40-60 words that directly answer the question
  quickAnswer: string;

  // Key points with icons
  keyPoints?: KeyPoint[];

  // Extended explanation paragraphs
  extendedContent?: React.ReactNode;

  // Optional summary box
  summary?: {
    heading: string;
    content: string;
  };

  // Locale for any built-in labels
  locale?: Locale;
}

// Translations for built-in UI labels (if any are added in future)
// Currently the component receives all text via props, but this structure
// supports future locale-aware enhancements
const translations: Record<Locale, {
  summaryDefaultHeading: string;
  keyPointsAriaLabel: string;
}> = {
  en: {
    summaryDefaultHeading: 'Quick Summary',
    keyPointsAriaLabel: 'Key points about this topic',
  },
  de: {
    summaryDefaultHeading: 'Kurze Zusammenfassung',
    keyPointsAriaLabel: 'Wichtige Punkte zu diesem Thema',
  },
  'pt-BR': {
    summaryDefaultHeading: 'Resumo Rápido',
    keyPointsAriaLabel: 'Pontos principais sobre este tema',
  },
  'ms-MY': {
    summaryDefaultHeading: 'Ringkasan Pantas',
    keyPointsAriaLabel: 'Perkara penting tentang topik ini',
  },
  es: {
    summaryDefaultHeading: 'Resumen Rápido',
    keyPointsAriaLabel: 'Puntos clave sobre este tema',
  },
  fr: {
    summaryDefaultHeading: 'Résumé Rapide',
    keyPointsAriaLabel: 'Points essentiels sur ce sujet',
  },
  ja: {
    summaryDefaultHeading: '概要',
    keyPointsAriaLabel: 'このトピックの重要ポイント',
  },
  id: {
    summaryDefaultHeading: 'Ringkasan Singkat',
    keyPointsAriaLabel: 'Poin-poin penting tentang topik ini',
  },
};

// Card styling based on color theme
const cardStyles = {
  accent: {
    bg: 'bg-accent-50 dark:bg-accent-900/20',
    border: 'border-accent-200 dark:border-accent-800',
    iconBg: 'bg-accent-100 dark:bg-accent-900/40',
    iconColor: 'text-accent-600 dark:text-accent-400',
    text: 'text-accent-800 dark:text-accent-200',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    iconBg: 'bg-green-100 dark:bg-green-900/40',
    iconColor: 'text-green-600 dark:text-green-400',
    text: 'text-green-800 dark:text-green-200',
  },
  yellow: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    iconBg: 'bg-yellow-100 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    text: 'text-yellow-800 dark:text-yellow-200',
  },
  neutral: {
    bg: 'bg-neutral-50 dark:bg-neutral-800/50',
    border: 'border-neutral-200 dark:border-neutral-700',
    iconBg: 'bg-neutral-100 dark:bg-neutral-800',
    iconColor: 'text-neutral-600 dark:text-neutral-400',
    text: 'text-neutral-700 dark:text-neutral-300',
  },
};

/**
 * KeyAnswer - Featured snippet-optimized answer section
 *
 * Designed to:
 * 1. Provide a direct 40-60 word answer (featured snippet target)
 * 2. Display key points in attractive cards with icons
 * 3. Expand with detailed content for depth
 *
 * SEO Note: The quickAnswer paragraph should directly answer the target
 * PAA question in a concise format that Google can extract.
 */
export const KeyAnswer: React.FC<KeyAnswerProps> = ({
  id = 'key-answer',
  heading,
  quickAnswer,
  keyPoints = [],
  extendedContent,
  summary,
  locale = 'en',
}) => {
  const t = translations[locale] || translations.en;

  return (
    <section id={id} className="py-12 md:py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* H2 Heading */}
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-6">
          {heading}
        </h2>

        {/* Quick Answer - Featured Snippet Target */}
        <p className="text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed mb-8">
          {quickAnswer}
        </p>

        {/* Key Points - Card Grid */}
        {keyPoints.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
            aria-label={t.keyPointsAriaLabel}
          >
            {keyPoints.map((point, index) => {
              const IconComponent = point.icon || CheckCircle;
              const colorKey = point.iconColor || 'accent';
              const styles = cardStyles[colorKey];
              return (
                <div
                  key={index}
                  className={`${styles.bg} ${styles.border} border rounded-xl p-5 transition-transform hover:scale-[1.02]`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`${styles.iconBg} rounded-lg p-2.5 flex-shrink-0`}
                    >
                      <IconComponent size={22} className={styles.iconColor} />
                    </div>
                    <p className={`${styles.text} text-sm leading-relaxed font-medium`}>
                      {point.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Extended Content */}
        {extendedContent && (
          <div className="prose prose-neutral dark:prose-invert max-w-none mb-8">
            {extendedContent}
          </div>
        )}

        {/* Summary Box */}
        {summary && (
          <div className="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <Info
                size={24}
                className="flex-shrink-0 text-accent-600 dark:text-accent-400 mt-0.5"
              />
              <div>
                <h3 className="font-semibold text-accent-900 dark:text-accent-100 mb-2">
                  {summary.heading}
                </h3>
                <p className="text-accent-800 dark:text-accent-200">
                  {summary.content}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default KeyAnswer;
